/**
 * StartPage - Tier & mode confirmation before onboarding
 */
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Shield, Heart, Sparkles, Users, User } from 'lucide-react';
import Header from '../components/shared/Header';

const TIER_INFO = {
  core: {
    level: 'CORE',
    icon: Shield,
    price: 97,
    individual: {
      title: 'Core Clarity',
      summary: 'A guided self-reflection experience to clarify your needs, values, and expectations before involving anyone else. Includes your Marrsing Alignment Score.',
    },
    couple: {
      title: 'Core Clarity',
      summary: 'Guided self-reflection for each partner to clarify values, needs, and expectations \u2014 preparing you for aligned conversations together. Includes your Marrsing Alignment Score.',
    },
  },
  ritual: {
    level: 'RITUAL',
    icon: Heart,
    price: 197,
    individual: {
      title: 'Ritual Structure',
      summary: 'Everything in Core, plus reflection prompts, structured clarity practices, and an ongoing check-in rhythm to build intentional relational readiness. Includes your Marrsing Alignment Score.',
    },
    couple: {
      title: 'Ritual Structure',
      summary: 'Everything in Core, plus the Clarity Conversation\u2122 experience, guided partner exchange, and ongoing check-in rhythms. Designed for couples building alignment \u2014 before defining their full agreement.',
    },
  },
  legacy: {
    level: 'LEGACY',
    icon: Sparkles,
    price: 297,
    individual: {
      title: 'Legacy Vision',
      summary: 'Everything in Ritual, plus a personal long-term relationship vision, conscious commitment framework, and preparation for future shared agreements.',
    },
    couple: {
      title: 'Legacy Vision',
      summary: 'Everything in Ritual, plus your MarrsingClause\u00AE Agreement, private responses locked before reveal, Certificate of Conscious Commitment, and a unique Relationship ID.',
    },
  },
};

const StartPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'couple';
  const tier = searchParams.get('tier') || 'core';
  const info = TIER_INFO[tier];

  if (!info) {
    navigate('/');
    return null;
  }

  const content = info[mode] || info.couple;
  const Icon = info.icon;
  const isCouple = mode === 'couple';

  const handleContinue = () => {
    navigate(`/onboarding?mode=${mode}&tier=${tier}`);
  };

  return (
    <div className="min-h-screen" style={{ background: '#F7F5F2' }}>
      <Header />

      <div
        className="flex items-center justify-center px-4 sm:px-6 py-12 sm:py-20"
        style={{ minHeight: 'calc(100vh - 100px)' }}
      >
        <div
          className="w-full max-w-lg rounded-2xl p-8 sm:p-10"
          style={{
            background: '#FFFFFF',
            border: '1px solid #E8E5E0',
            boxShadow: '0 4px 24px rgba(0,0,0,0.05)',
          }}
          data-testid="start-confirmation-card"
        >
          {/* Mode badge */}
          <p className="text-xs uppercase tracking-widest font-medium mb-4" style={{ color: '#C9A84C' }}>Step 2</p>
          <div className="flex items-center gap-2 mb-6">
            {isCouple ? (
              <Users className="w-4 h-4 text-stone-500" />
            ) : (
              <User className="w-4 h-4 text-stone-500" />
            )}
            <span
              className="text-xs uppercase tracking-widest font-medium text-stone-500"
              data-testid="start-mode-label"
            >
              {mode} path
            </span>
          </div>

          {/* Icon + Tier */}
          <div className="flex items-center gap-3 mb-4">
            <Icon className="w-7 h-7" style={{ color: '#1A1A1A' }} />
            <div>
              <h2
                className="text-2xl sm:text-3xl font-bold"
                style={{ color: '#1A1A1A' }}
                data-testid="start-tier-title"
              >
                {content.title}
              </h2>
              <p className="text-xs uppercase tracking-widest text-stone-400 font-medium">
                {info.level}
              </p>
            </div>
          </div>

          {/* Summary */}
          <p
            className="text-sm sm:text-base text-stone-600 leading-relaxed mb-6"
            data-testid="start-summary"
          >
            {content.summary}
          </p>

          {/* Price */}
          <div
            className="rounded-xl p-4 mb-8 flex items-center justify-between"
            style={{ background: '#F7F5F2' }}
          >
            <span className="text-sm text-stone-500 font-medium">One-time investment</span>
            <span
              className="text-2xl font-bold"
              style={{ color: '#1A1A1A' }}
              data-testid="start-price"
            >
              ${info.price}
            </span>
          </div>

          {/* CTAs */}
          <button
            onClick={handleContinue}
            className="w-full rounded-full py-3.5 text-base font-semibold transition-all duration-200 hover:scale-[1.02] active:scale-[0.97] flex items-center justify-center gap-2 mb-3"
            style={{ background: '#2F9ED8', color: '#fff' }}
            data-testid="start-continue-btn"
          >
            {isCouple ? 'Continue Together' : 'Continue'}
            <ArrowRight className="w-4 h-4" />
          </button>

          <button
            onClick={() => navigate('/')}
            className="w-full rounded-full py-3 text-sm font-medium transition-all duration-200 hover:bg-stone-100 flex items-center justify-center gap-2"
            style={{ color: '#78716c' }}
            data-testid="start-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Plans
          </button>
        </div>
      </div>
    </div>
  );
};

export default StartPage;
